import { HttpResponse, http, delay as mswDelay } from 'msw';
import type {
  CreateResponse,
  ExecuteMultipleResponseDto,
  InvoiceContactMarketPartyDetailsRequest,
} from '../generated/model';
import { Product } from '../generated/model';
import { createOrganizationInformation } from './account';
import { createAttachment } from './attachment';
import { createSurveyDialogues } from './ces';
import { createComments } from './comments';
import {
  createConnectionInformation,
  createRegionalGridOperator,
  createRegionalGridOperators,
  generateEan,
} from './connection';
import { createContacts } from './contact';
import { createContactDetails, createContactsDetails } from './contactdetails';
import {
  createCprContactGroups,
  createCprContactResponse,
  createCprContactResponses,
  createCprContacts,
  createCprFilters,
  createCprManagementContacts,
  createCprValidationEventRequest,
} from './cpr';
import {
  createInvoiceContactDetails,
  createInvoiceMarketParties,
} from './invoiceContactMarketParty';
import {
  createKnowledgeArticleDetail,
  createKnowledgeArticleListResult,
} from './knowledgeBase';
import { createMarketPartyInformation } from './marketPartyInformation';
import { createMarketPartyRegistryOrganisations } from './marketparty-organisations';
import { createNotification } from './notifications';
import { createPreferredParty } from './preferredParty';
import { createSurveyDialogue } from './surveyDialogue';
import { createTeams } from './team';
import { createTeamDetails } from './teamdetails';
import {
  createMainCategories,
  createRejectionCategories,
  createTicket,
  createTicketFilters,
  createTickets,
  createTicketTrends,
} from './ticket';

async function delay(duration: number): Promise<void> {
  // Ensure there is no response delay in tests.
  return import.meta.env.MODE === 'test' ? undefined : mswDelay(duration);
}

export const handlers = [
  http.post('*/attachment', () =>
    HttpResponse.json({
      bytes: 1000000,
      id: '123',
      mimeType: 'application/pdf',
      url: '/',
    }),
  ),
  http.get('*/attachment/metadata', () =>
    HttpResponse.json({
      items: [{ bytes: 12345, fileName: 'file.txt', id: '123' }],
      totalCount: 1,
    }),
  ),
  http.get('*/comment', async () => {
    await delay(100);
    return HttpResponse.json(createComments());
  }),
  http.post('*/comment/create', () => HttpResponse.json({ id: '123' })),
  http.get('*/connection-information', ({ request }) => {
    const url = new URL(request.url);
    const isGasEan = url.searchParams.get('ean18') === generateEan(18, 'gas');
    return HttpResponse.json(
      createConnectionInformation({
        ean18: url.searchParams.get('ean18')?.toString(),
        failureEventCode: isGasEan ? '14.1' : undefined, // 808679959269303857
        product: isGasEan ? Product.Gas : Product.Electricity,
        regionalGridOperator: isGasEan
          ? createRegionalGridOperator({
              ean13: generateEan(13, 'grid-operator 2'),
              id: 'c366d5f9-5d3e-404e-ae55-5ae6b3342ea4',
              name: 'Stedin',
            })
          : createRegionalGridOperator(),
      }),
    );
  }),
  http.get('*/contact', () => HttpResponse.json(createContacts())),
  http.get('*/contact/ticket-handlers', () =>
    HttpResponse.json(createContacts()),
  ),
  http.get('*/grid-operator', async () => {
    await delay(100);
    return HttpResponse.json(createRegionalGridOperators());
  }),
  http.get('*/main-category', async () => {
    await delay(100);
    return HttpResponse.json(createMainCategories());
  }),
  http.get('*/rejection-category', async () => {
    await delay(100);
    return HttpResponse.json(createRejectionCategories());
  }),
  http.get('*/team', async () => {
    await delay(100);
    return HttpResponse.json(createTeams());
  }),
  http.get('*/team/:id', async () => {
    await delay(100);
    return HttpResponse.json(createTeamDetails());
  }),
  http.post('*/ticket', async () => {
    return HttpResponse.json(createTickets());
  }),
  http.post('*/attachment', async () => HttpResponse.json(createAttachment())),
  http.post('*/survey/create', async () =>
    HttpResponse.json({
      failedIds: [],
      success: true,
    } satisfies ExecuteMultipleResponseDto),
  ),
  http.post('*/ticket/close', async () =>
    HttpResponse.json({
      failedIds: [],
      success: true,
    } satisfies ExecuteMultipleResponseDto),
  ),
  http.post('*/ticket/call-to-action', () => HttpResponse.json({ id: 123 })),
  http.post('*/ticket/export', async () => {
    await delay(100);
    return new HttpResponse('', { headers: { 'Content-Type': 'text/csv' } });
  }),
  http.post('*/ticket/create', async () => {
    return HttpResponse.json({ id: '86ce5cdb-03bb-a83c-3363-50b132480f07' });
  }),
  http.get('*/ticket/filters', () => HttpResponse.json(createTicketFilters())),
  http.get('*/ticket/:id', async ({ params }) => {
    const { id } = params;
    const baseTicket = createTickets().items.find((a) => a.id === id);
    if (baseTicket) {
      return HttpResponse.json(createTicket({ ...baseTicket }));
    }
    return HttpResponse.json(createTicket({ id: id as string }));
  }),
  http.get('*/ticket/trends', () => HttpResponse.json(createTicketTrends())),
  http.get('*/user/organisation-information', () =>
    HttpResponse.json(createOrganizationInformation()),
  ),
  http.post('*/knowledge-article', async ({ request }) => {
    const body = (await request.json()) as { filter?: any };
    const { filter } = body;
    let allArticles = createKnowledgeArticleListResult().items;

    if (filter?.mainCategoryIds) {
      allArticles = allArticles.filter((a) =>
        filter.mainCategoryIds.includes(a.mainCategory.id),
      );
    }
    if (filter?.subCategoryIds) {
      allArticles = allArticles.filter((a) =>
        filter.subCategoryIds.includes(a.subCategory?.id),
      );
    }

    return HttpResponse.json({ items: allArticles });
  }),
  http.get('*/knowledge-article/categories', async () =>
    HttpResponse.json(createMainCategories()),
  ),
  http.get('*/knowledge-article/:id', async ({ params }) => {
    const { id } = params;
    const baseArticle = createKnowledgeArticleListResult().items.find(
      (a) => a.id === id,
    );

    if (baseArticle) {
      return HttpResponse.json(
        createKnowledgeArticleDetail({ ...baseArticle }),
      );
    }
    return HttpResponse.json(
      createKnowledgeArticleDetail({ id: id as string }),
    );
  }),
  http.post('*/survey', async () => {
    await delay(100);
    return HttpResponse.json(createSurveyDialogues());
  }),
  http.get('*/survey/:id', () => HttpResponse.json(createSurveyDialogue())),
  http.post('*/cpr-contact', () => HttpResponse.json(createCprContacts())),
  http.post('*/cpr-organisation', () => HttpResponse.json(createCprContacts())),
  http.post('*/cpr-management-contact', () =>
    HttpResponse.json(createCprManagementContacts()),
  ),
  http.post('*/cpr-management-contact/contact', () =>
    HttpResponse.json(createCprContactResponses()),
  ),
  http.get('*/cpr-contact-group', () =>
    HttpResponse.json(createCprContactGroups()),
  ),
  http.get('*/cpr-management-contact/contact/:id', ({ params }) =>
    HttpResponse.json(createCprContactResponse({ id: params.id as string })),
  ),
  http.post('*/cpr-management-contact/create', () =>
    HttpResponse.json({ id: '1' } satisfies CreateResponse),
  ),
  http.post('*/cpr-management-contact/contact/create', () =>
    HttpResponse.json({ id: '1' } satisfies CreateResponse),
  ),
  http.put('*/cpr-management-contact/contact/:id', () => HttpResponse.text('')),
  http.post('*/cpr-management-contact/:id', () =>
    HttpResponse.json(createCprManagementContacts()),
  ),
  http.delete('*/cpr-management-contact/:id', ({ params }) =>
    HttpResponse.json({ id: params.id as string } satisfies CreateResponse),
  ),
  http.post('*/cpr-management-contact/cprvalidationevent', () =>
    HttpResponse.json(createCprValidationEventRequest()),
  ),
  http.get('*/cpr-contact/filters', () =>
    HttpResponse.json(createCprFilters()),
  ),
  http.post('*/cpr-contact/export', async () => {
    await delay(100);
    return new HttpResponse('', { headers: { 'Content-Type': 'text/csv' } });
  }),
  http.post('*/cpr-organisation/export', async () => {
    await delay(100);
    return new HttpResponse('', { headers: { 'Content-Type': 'text/csv' } });
  }),
  http.post('*/contact/details', () =>
    HttpResponse.json(createContactsDetails()),
  ),
  http.post('*/contact/details/create', () =>
    HttpResponse.json({ id: '1' } satisfies CreateResponse),
  ),
  http.put('*/contact/details/:id', () => HttpResponse.json({ id: '1' })),
  http.get('*/contact/details/:id', () =>
    HttpResponse.json(createContactDetails()),
  ),
  http.delete('*/contact/details/:id', () => HttpResponse.json({ id: '1' })),
  http.post('*/contact/details/export', async () => {
    await delay(100);
    return new HttpResponse('', { headers: { 'Content-Type': 'text/csv' } });
  }),
  http.get('*/notification', () => HttpResponse.json(createNotification())),
  http.put('*/notification', () => HttpResponse.text('')),
  http.post('*/notification/sub-category', () => HttpResponse.text('')),
  http.delete('*/notification/sub-category/:id', () => HttpResponse.text('')),
  http.post('*/dataRequest/create', () => HttpResponse.json({ id: '1' })),
  http.post('*/dataRequest/create/simple', () =>
    HttpResponse.json({ id: '1' }),
  ),
  http.post('*/marketparty-registry/invoice-contact-market-parties', () =>
    HttpResponse.json(createInvoiceMarketParties()),
  ),
  http.get('*/marketparty-registry/invoice-contact-market-parties', () =>
    HttpResponse.json(createInvoiceMarketParties()),
  ),
  http.post(
    '*/marketparty-registry/invoice-contact-market-parties/:mprid/details',
    async ({ params, request }) => {
      const mprid = params.mprid as string;
      const requestBody =
        (await request.json()) as InvoiceContactMarketPartyDetailsRequest;

      return HttpResponse.json(createInvoiceContactDetails(mprid, requestBody));
    },
  ),
  http.get('*/marketparty-registration', () =>
    HttpResponse.json(createMarketPartyInformation()),
  ),
  http.get('*/marketparty-registration/market-parties', () =>
    HttpResponse.json(createInvoiceMarketParties()),
  ),
  http.get('*/marketparty-registry/organization-preferences', () =>
    HttpResponse.json(createPreferredParty()),
  ),
  http.post('*/marketparty-registry/organisations', () =>
    HttpResponse.json(createMarketPartyRegistryOrganisations()),
  ),
];
